import React, { FC } from 'react';

import {
  Box,
  Center,
  Divider,
  HStack,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { customTheme } from 'theme';

import { SoftwareInventorySummary } from 'app/api/services/scanVaultSoftware';
import {
  CustomTooltip,
  formatTooltip,
  getIcon,
  PageHeaderWithIcon,
} from 'components/DataDisplay';

import {
  formatSoftwareCount,
  getSoftwareCategoryIcon,
  getSoftwareCategoryLabel,
  getSoftwareEntityCount,
  getSoftwareSubcategories,
  getSoftwareSubcategoryIcon,
  getSoftwareSubcategoryLabel,
  OPERATING_SYSTEM_CATEGORY,
  sortSoftwareCategories,
} from '../utils';

interface Props {
  summary: SoftwareInventorySummary;
  onSelectSubcategory?: (category: string, subcategory: string) => void;
}

export const SoftwareInventoryOverview: FC<Props> = ({
  summary,
  onSelectSubcategory,
}) => (
  <Box p={6} overflowY="auto" h="100%">
    <Stack spacing={5}>
      {sortSoftwareCategories(summary.categories).map(category => (
        <Stack key={category.category} spacing={3}>
          <HStack w="full">
            <PageHeaderWithIcon
              label={getSoftwareCategoryLabel(category.category)}
              icon={getIcon(getSoftwareCategoryIcon(category.category))}
              fontSize="lg"
              iconSize="sm"
              fontWeight={600}
              reversed
              border
              iconBgColor="white"
              useCustomColor
              iconColor="primary"
            />
            <Box color={customTheme.colors.gray[300]}>
              [
              {formatSoftwareCount(
                getSoftwareEntityCount(category.category, category),
              )}{' '}
              {category.category === OPERATING_SYSTEM_CATEGORY
                ? 'releases'
                : 'packages'}
              ]
            </Box>
          </HStack>
          <Divider borderColor={customTheme.colors.gray[100]} />
          <Wrap spacing={3} pt={1}>
            {getSoftwareSubcategories(category).map(subcategory => (
              <WrapItem key={`${category.category}:${subcategory.key}`}>
                <CustomTooltip
                  label={formatTooltip({
                    header: getSoftwareSubcategoryLabel(
                      category.category,
                      subcategory.key,
                    ),
                    content: (
                      <Stack spacing={0} w="full">
                        <Box>
                          Category:{' '}
                          {getSoftwareCategoryLabel(category.category)}
                        </Box>
                        <Box>
                          Resources:{' '}
                          {formatSoftwareCount(subcategory.resource_count)}
                        </Box>
                        <Box>
                          Packages:{' '}
                          {formatSoftwareCount(
                            getSoftwareEntityCount(
                              category.category,
                              subcategory,
                            ),
                          )}
                        </Box>
                        <Box>
                          Installations:{' '}
                          {formatSoftwareCount(subcategory.installation_count)}
                        </Box>
                      </Stack>
                    ),
                    width: 64,
                  })}
                  placement="right"
                >
                  <Box
                    border="1px solid"
                    borderRadius={6}
                    borderColor={customTheme.colors.gray[100]}
                    w="125px"
                    py={2}
                    px={2}
                    bg="white"
                    h="130px"
                    cursor={onSelectSubcategory ? 'pointer' : undefined}
                    onClick={() =>
                      onSelectSubcategory?.(category.category, subcategory.key)
                    }
                  >
                    <Stack justify="space-between" h="full">
                      <HStack>
                        <Center boxSize={5} color="primary">
                          {getIcon(
                            getSoftwareSubcategoryIcon(
                              category.category,
                              subcategory.key,
                            ),
                          )}
                        </Center>
                      </HStack>
                      <Box w="full">
                        <Center fontWeight={600} textAlign="center">
                          <Text noOfLines={2} textOverflow="ellipsis">
                            {getSoftwareSubcategoryLabel(
                              category.category,
                              subcategory.key,
                            )}
                          </Text>
                        </Center>
                      </Box>
                      <Center fontSize="lg" w="full">
                        {formatSoftwareCount(
                          getSoftwareEntityCount(
                            category.category,
                            subcategory,
                          ),
                        )}
                      </Center>
                    </Stack>
                  </Box>
                </CustomTooltip>
              </WrapItem>
            ))}
          </Wrap>
        </Stack>
      ))}
    </Stack>
  </Box>
);
